import React from 'react';
import {
  MdDomain, MdSecurity, MdDns, MdOutlineShoppingCart, MdOutlineNfc, MdEmail,
  MdDashboardCustomize, MdPayments, MdInventory2, MdReceiptLong, MdGroups, MdWebhook,
} from 'react-icons/md';
import { glassStyle } from './themes';

/**
 * What the platform actually does.
 *
 * Every line here maps to something shipped — the dashboard sections, the
 * storefront, the domain flow. Two earlier entries were removed for
 * overclaiming: "Full DNS control... A, CNAME, MX, TXT" described a DNS host,
 * which this is not (you point your existing DNS at us and we verify it), and
 * "any chain" for NFTs. Specific and true beats broad and wrong — the people
 * who care enough to read a feature grid are exactly the ones who will check.
 */
const GROUPS = [
  {
    heading: 'Your site, your address',
    items: [
      {
        icon: MdDashboardCustomize, color: '#A78BFA', title: 'Page builder',
        desc: 'Compose pages from sections and components across header, main and footer. Draft, preview, publish — or start from a template.',
      },
      {
        icon: MdDns, color: '#60A5FA', title: 'Custom domains',
        desc: 'Point your DNS at us, verify with a TXT record, and your site runs on your own URL. HTTPS certificates are issued and renewed automatically.',
      },
      {
        icon: MdDomain, color: '#22D3EE', title: 'Subdomain routing',
        desc: 'Send shop.yourbrand.com to the storefront, help.yourbrand.com to a page, and park the rest — one rule per subdomain.',
      },
    ],
  },
  {
    heading: 'Selling',
    items: [
      {
        icon: MdOutlineShoppingCart, color: '#F59E0B', title: 'Storefront & checkout',
        desc: 'Products with variants, cart, checkout, customer accounts, wishlists, reviews and order tracking — no separate store to wire up.',
      },
      {
        icon: MdPayments, color: '#34D399', title: 'Payments on your account',
        desc: 'Start on the platform Stripe with sandbox cards, then connect your own keys when you go live. Money lands in your account, not ours.',
      },
      {
        icon: MdInventory2, color: '#F472B6', title: 'Inventory & warehouses',
        desc: 'Stock levels per warehouse, transfers between them, movement history and low-stock alerts before you sell something you cannot ship.',
      },
      {
        icon: MdReceiptLong, color: '#FBBF24', title: 'Tax & invoicing',
        desc: 'Tax rules per country and region, inclusive or exclusive pricing, compound rates, and invoices generated from real orders.',
      },
    ],
  },
  {
    heading: 'Running the business',
    items: [
      {
        icon: MdGroups, color: '#818CF8', title: 'Brands & teams',
        desc: 'Several brands under one login, each with its own domain, catalogue and members. Invite people and scope what they can reach.',
      },
      {
        icon: MdEmail, color: '#A78BFA', title: 'Transactional email',
        desc: 'Order and account emails go out of the box. Connect your own SendGrid to send from your domain with your own templates.',
      },
      {
        icon: MdOutlineNfc, color: '#34D399', title: 'NFT studio',
        desc: 'Design a collection, deploy the contract and run drops beside the rest of the brand, instead of in a separate tool.',
      },
      {
        icon: MdWebhook, color: '#38BDF8', title: 'API keys & webhooks',
        desc: 'Subscribe to order and inventory events, or call the API directly. Deliveries are logged with status and retry so you can see what failed.',
      },
      {
        icon: MdSecurity, color: '#F87171', title: 'Roles & audit trail',
        desc: 'Per-section permissions, an activity log of who changed what, and account controls that take effect on the next request.',
      },
    ],
  },
];

export default function Features({ t }) {
  return (
    <div id='features' style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px', position: 'relative', zIndex: 1 }}>
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: '0 0 14px' }}>
          Everything the brand needs, already connected
        </h2>
        <p style={{ color: t.textSub, fontSize: 18, margin: 0, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
          The usual stack is a site builder, a store, a payment processor, an email tool
          and a spreadsheet. Here they are one system, so an order already knows about
          the stock, the tax and the customer.
        </p>
      </div>

      {GROUPS.map((group) => (
        <div key={group.heading} style={{ marginBottom: 44 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <span style={{ color: t.text, fontWeight: 700, fontSize: 15, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
              {group.heading}
            </span>
            <span style={{ flex: 1, height: 1, background: t.border }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 18 }}>
            {group.items.map((f) => (
              <div
                key={f.title}
                style={{ ...glassStyle(t), padding: 24, transition: 'all 0.25s', cursor: 'default' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceHover; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = t.surface; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ width: 48, height: 48, borderRadius: 12, background: `${f.color}22`, border: `1px solid ${f.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <f.icon size={22} color={f.color} />
                </div>
                <div style={{ color: t.text, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{f.title}</div>
                <p style={{ color: t.textSub, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
